import { useEffect, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Button } from '@jeraisonne/ui';
import { AlertCircle } from 'lucide-react';
import { loadExerciseDemo } from '@/features/demo/exercise-demo-loader';
import { loadExerciseDemoV2, type DemoExerciseV2 } from '@/features/demo/exercise-demo-loader-v2';
import { ExerciseScreen } from '@/features/demo/screens/exercise-screen';
import { DemoEndScreen } from '@/features/demo/screens/demo-end-screen';
import {
  readDecouverteProfile,
  saveDecouverteProfile,
} from '@/features/decouverte/decouverte-profile';
import { trackDemoEvent } from '@/features/demo/tracking';
import type { DemoExercise } from '@/features/demo/types';
import { ExercisePlayerV2 } from '@/features/exercise-player-v2';

/**
 * Route `/demo/exercise/$exerciseId` — aperçu anonyme d'un exercice précis.
 *
 * Lancée par le bouton « Tester » des cartes d'exercice du site marketing.
 * Aucune authentification : le visiteur joue les 2 premières étapes guidées
 * (RPC `get_exercise_demo`), puis tombe sur l'écran de fin de démo
 * (`DemoEndScreen` : inscription gratuite, aucun prix).
 */
export function DemoExercisePage() {
  const { exerciseId } = useParams({ strict: false }) as { exerciseId: string };
  const navigate = useNavigate();

  const [exercise, setExercise] = useState<DemoExercise | null>(null);
  const [exerciseV2, setExerciseV2] = useState<DemoExerciseV2 | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [phase, setPhase] = useState<'exercise' | 'paywall'>('exercise');
  // Incrémenté au « Recommencer » pour remonter ExerciseScreen (reset interne).
  const [runKey, setRunKey] = useState(0);

  useEffect(() => {
    trackDemoEvent({ type: 'demo_started' });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setExercise(null);
    setExerciseV2(null);
    setLoadError(false);
    setPhase('exercise');
    // On essaie d'abord la RPC V2 (anon-callable). Si elle renvoie null →
    // l'exo n'est pas V2 → on tombe sur le loader V1 historique.
    loadExerciseDemoV2(exerciseId)
      .then((v2) => {
        if (cancelled) return;
        if (v2) {
          setExerciseV2(v2);
          return;
        }
        return loadExerciseDemo(exerciseId).then((v1) => {
          if (!cancelled) setExercise(v1);
        });
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [exerciseId]);

  function restart() {
    setPhase('exercise');
    setRunKey((k) => k + 1);
  }

  if (loadError) {
    return (
      <Shell>
        <div className="mx-auto max-w-lg space-y-4">
          <div className="bg-error-50 border-error-100 flex items-start gap-2 rounded-xl border px-4 py-3">
            <AlertCircle className="text-error-600 mt-0.5 h-4 w-4 shrink-0" />
            <p className="text-error-700 text-sm">
              Cet exercice n&apos;est pas disponible pour le moment.
            </p>
          </div>
          <Button onClick={() => void navigate({ to: '/demo' })} fullWidth>
            Découvrir la démo guidée
          </Button>
        </div>
      </Shell>
    );
  }

  if (!exercise && !exerciseV2) {
    return (
      <Shell>
        <div className="mx-auto max-w-lg space-y-3">
          <div className="bg-ink-100 h-6 w-40 animate-pulse rounded" />
          <div className="bg-ink-100 h-32 animate-pulse rounded-2xl" />
          <div className="bg-ink-100 h-12 animate-pulse rounded-xl" />
        </div>
      </Shell>
    );
  }

  if (phase === 'paywall') {
    return (
      <div className="min-h-dvh bg-[#061433] sm:flex sm:items-center sm:justify-center sm:py-8">
        <DemoEndScreen
          restartLabel="Recommencer"
          onRestart={restart}
          onSignup={() => {
            trackDemoEvent({ type: 'demo_signup_clicked' });
            if (!readDecouverteProfile()) saveDecouverteProfile('student');
            void navigate({ to: '/login', search: { inscription: 1 } });
          }}
          onLogin={() => void navigate({ to: '/login' })}
        />
      </div>
    );
  }

  // Aperçu V2 (route le player V2 en mode démo : tronqué côté serveur à
  // 3 questions, mutations court-circuitées, paywall après la dernière).
  if (exerciseV2) {
    return (
      <Shell>
        <ExercisePlayerV2
          key={runKey}
          exerciseId={exerciseV2.exercise.id}
          demoMode
          demoPayload={{
            exercise: {
              ...exerciseV2.exercise,
              chapter_id: exerciseV2.exercise.chapter_id ?? undefined,
            },
            blocks: exerciseV2.blocks,
            access: { allowed: true },
            attempt_id: null,
          }}
          onDemoComplete={() => {
            trackDemoEvent({ type: 'demo_completed' });
            setPhase('paywall');
          }}
        />
      </Shell>
    );
  }

  return (
    <Shell>
      <ExerciseScreen
        key={runKey}
        exercise={exercise!}
        alreadyAnswered={[]}
        onAnswer={() => {}}
        onBack={() => window.history.back()}
        onComplete={() => {
          trackDemoEvent({ type: 'demo_completed' });
          setPhase('paywall');
        }}
      />
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-dvh bg-transparent">
      <div className="native:pt-[calc(env(safe-area-inset-top)+1.5rem)] mx-auto max-w-2xl px-4 py-6 pb-[max(env(safe-area-inset-bottom),1.5rem)] sm:py-10">
        {children}
      </div>
    </div>
  );
}
